import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { repAPI } from '../../services/api';

const CONTACT_ROLES = ['VET', 'VET_TECH', 'BILLING', 'ADMIN', 'OTHER'];

const LeadLoginRequestDialog = ({ open, onClose, lead, contact, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'OTHER',
    hasPortalAccess: true,
    canPlaceOrders: false,
    canPayInvoices: false,
    canViewReports: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (contact && open) {
      // Pre-fill from contact if available
      setFormData({
        email: contact.email || '',
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        phone: contact.phone || '',
        role: contact.role || 'OTHER',
        hasPortalAccess: true,
        canPlaceOrders: false,
        canPayInvoices: false,
        canViewReports: false,
      });
    } else if (open) {
      // Reset for standalone request
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'OTHER',
        hasPortalAccess: true,
        canPlaceOrders: false,
        canPayInvoices: false,
        canViewReports: false,
      });
    }
    setError('');
  }, [contact, open]);

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.email || !formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.firstName || !formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Invalid email format');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const requestData = {
        leadContactId: contact?.id || null,
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone?.trim() || null,
        role: formData.role,
        hasPortalAccess: formData.hasPortalAccess,
        canPlaceOrders: formData.canPlaceOrders,
        canPayInvoices: formData.canPayInvoices,
        canViewReports: formData.canViewReports,
      };

      await repAPI.requestLeadLogin(lead.id, requestData);
      
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to create login request');
    } finally {
      setLoading(false);
    }
  };

  const formatRole = (role) => {
    return role
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Request Login Access
        {contact && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: 'Poppins, sans-serif' }}>
            For: {contact.firstName} {contact.lastName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Email *"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            disabled={loading || !!contact}
            required
            fullWidth
            helperText={contact ? "Email from contact (can be edited if needed)" : "Email for the login account"}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="First Name *"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              disabled={loading || !!contact}
              required
            />
            <TextField
              label="Last Name *"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              disabled={loading || !!contact}
              required
            />
          </Box>
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            disabled={loading || !!contact}
            fullWidth
          />
          <TextField
            select
            label="Role *"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            disabled={loading}
            required
            fullWidth
          >
            {CONTACT_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {formatRole(role)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
              Permissions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hasPortalAccess}
                    onChange={(e) => setFormData({ ...formData, hasPortalAccess: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Portal Access"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.canPlaceOrders}
                    onChange={(e) => setFormData({ ...formData, canPlaceOrders: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Can Place Orders"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.canPayInvoices}
                    onChange={(e) => setFormData({ ...formData, canPayInvoices: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Can Pay Invoices"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.canViewReports}
                    onChange={(e) => setFormData({ ...formData, canViewReports: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Can View Reports"
              />
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 1 }}>
            This login request will be submitted for admin approval. Once approved, a user account will be created.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.email || !formData.firstName || !formData.lastName}
        >
          {loading ? <CircularProgress size={20} /> : 'Request Login'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadLoginRequestDialog;

